import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { axiosPrivate } from "../api/axios";
import useAuth from "./useAuth";
import useRefreshToken from "./useRefreshToken";

// Module-level refresh queue: ensures only ONE refresh request is in-flight at a time.
// If multiple 403s arrive simultaneously (e.g., several API calls fire when the access
// token has just expired), the first one kicks off the refresh and the rest wait for it.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const useAxiosPrivate = () => {
  let navigate = useNavigate();
  let location = useLocation();
  const refresh = useRefreshToken();
  const { auth } = useAuth();

  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers["Authorization"]) {
          //1st attempt
          config.headers["Authorization"] = "Bearer " + auth.accessToken;
        }
        if (config.method === "patch") {
          config.headers["Content-Type"] = "application/json-patch+json";
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      (error) => {
        const prevRequest = error.config;

        if (error.response && error.response.status === 403 && !prevRequest.sent) {
          // -----------------------------------------------------------------
          // If another refresh is already running, queue this request and wait
          // for the refresh to complete before retrying.
          // -----------------------------------------------------------------
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                prevRequest.headers["Authorization"] = "Bearer " + token;
                return axiosPrivate(prevRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          prevRequest.sent = true;
          isRefreshing = true;

          return new Promise((resolve, reject) => {
            refresh()
              .then((newAccessToken) => {
                isRefreshing = false;
                if (!newAccessToken) {
                  // refresh() already navigated to /login
                  processQueue(new Error("Session expired"), null);
                  reject(error);
                  return;
                }
                processQueue(null, newAccessToken);
                prevRequest.headers["Authorization"] = "Bearer " + newAccessToken;
                resolve(axiosPrivate(prevRequest));
              })
              .catch((err) => {
                isRefreshing = false;
                processQueue(err, null);
                reject(err);
              });
          });
        }

        if (error.response && error.response.status === 401) {
          navigate("/login", { state: { from: location }, replace: true });
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept);
      axiosPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [auth, refresh, navigate, location]);

  return axiosPrivate;
};

export default useAxiosPrivate;
